import { UseSuspenseQueryOptions, useMutation, useQueryClient } from '@tanstack/react-query';

import { Id, NamedEntity, PagedParams, PagedResponse, getQueryKey } from '@feature/utils';

import * as api from './DisciplineApi';
import { Discipline, DisciplineRequest } from './DisciplineTypes';

export const disciplineKey = getQueryKey('discipline');

export function getDisciplinePagedOptions(params: PagedParams): UseSuspenseQueryOptions<PagedResponse<Discipline>> {
  return {
    queryKey: disciplineKey.paged(params),
    queryFn: () => api.getPaged(params),
  };
}

export function getDisciplineOptions(disciplineId: Id): UseSuspenseQueryOptions<Discipline> {
  return {
    queryKey: disciplineKey.one(disciplineId),
    queryFn: () => api.get(disciplineId),
  };
}

export function getDisciplineByDepartmentOptions(departmentId: number): UseSuspenseQueryOptions<Discipline[]> {
  return {
    queryKey: disciplineKey.as(['department', departmentId]),
    queryFn: () => api.getDisciplinesByDepartment(departmentId),
  };
}

export function getDisciplineAsHeadOfOptions(): UseSuspenseQueryOptions<Discipline[]> {
  return {
    queryKey: disciplineKey.as(['list', 'head-of']),
    queryFn: () => api.getDisciplinesAsHeadOf(),
  };
}

export function getDisciplineUsersOptions(disciplineId: Id | undefined): UseSuspenseQueryOptions<NamedEntity[]> {
  return {
    queryKey: disciplineKey.as(['list', 'users', disciplineId]),
    queryFn: disciplineId != null ? () => api.getDisciplineUsers(disciplineId) : () => Promise.resolve([]),
  };
}

export function useDisciplineCreate() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (request: DisciplineRequest) => api.create(request),
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: disciplineKey.root(),
      }),
  });
}

export function useDisciplineEdit(disciplineId: Id) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (request: DisciplineRequest) => api.edit(disciplineId, request),
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: disciplineKey.root(),
      }),
  });
}
